import { PayloadAction } from '@reduxjs/toolkit'
import { QueryParameters } from '@deck.gl/carto'
import { RootState } from 'app/state/store'
import { getAttributeStats } from 'features/builder/api/builder'
import { addLayerRequest, addQueryRequest, showSQLPanel } from 'features/builder/state/builderSlice'
import { getDatasetSettingsForTable } from 'features/builder/state/builderSliceUtils'
import { RequestStatus } from 'features/common'
import { createApiError } from 'features/common/api/apiError'
import { Connection, getConnectionByName, ProviderType } from 'features/connections'
import { getDataExplorerObject } from 'features/data-explorer/api/dataExplorer'
import { reloadDataTreeItemRequest } from 'features/data-explorer/state/dataExplorerSlice'
import { DataExplorerTable, TreeObjectType } from 'features/data-explorer/types'
import { checkProcessingJob, createProcessingJob } from 'features/jobs/api/processingJobs'
import { JobStatusType, ProcessingJob, ProcessingJobStatus } from 'features/jobs/types'
import { Formats, MapType } from 'features/maps/types'
import { call, put, select, takeLatest } from 'redux-saga/effects'
import {
  addAnalysis as addAnalysisToLocalStorage,
  deleteAnalysis as deleteAnalysisFromLocalStorage,
  getAnalysis,
  getArrayAnalysisFromMap as getArrayAnalysesFromMap
} from '../api/analysisLocalStorageApi'
import { Analysis } from '../types'
import { generateLaunchQuery } from '../ui/NewAnalysisDialog/AnalysisCatalog/catalog'
import {
  addAnalysis,
  checkAnalysesRequest,
  fetchColumnStatsRequest,
  launchAnalysisRequest,
  analysisToSQLPanelRequest,
  removeAnalysis,
  setAnalysis,
  setColumnStats,
  setColumnStatsError,
  setColumnStatsStatus,
  setSecondSourceTable,
  QuerySource
} from './analysisSlice'
import { analysisCreatedEventRequest } from 'features/metrics/state/metricsSlice'
import { getEnrichedDatasetById } from 'features/builder/state/keplerGlSlice'
import { parseQuery } from 'features/builder/utils/queryParametersUtils'
import { getClient } from 'features/common/utils/clientParameter'
import { pollJobUntilFinished } from 'features/jobs/state/jobsSagas'

function* analysisToSQLPanel(action: PayloadAction<boolean>) {
  try {
    const shouldExecute = action.payload
    const connection = yield select((state: RootState) => state.analysis.connection)
    const { query }: QuerySource = yield select((state: RootState) => state.analysis.querySource)
    const sqlParameters = yield select((state: RootState) => state.builder.sqlParameters)
    const mapId = yield select((state: RootState) => state.maps.currentMap?.id)
    const analysis = yield select((state: RootState) => state.analysis.selectedAnalysis)
    const { name, provider_id } = connection
    const type = MapType.query
    const format = Formats.TILEJSON

    if (shouldExecute) {
      yield put(
        addQueryRequest({
          mapId,
          connection: name,
          provider: provider_id,
          source: query,
          sqlParameters,
          type,
          format,
          minimized: Boolean(shouldExecute),
          fromAnalysis: true
        })
      )
    } else {
      yield put(
        showSQLPanel({
          query,
          mapId,
          connection: name,
          provider: provider_id,
          minimized: Boolean(shouldExecute),
          fromAnalysis: true
        })
      )
    }
    const metricEvent = {
      mapId: mapId,
      analysisType: analysis,
      createdAs: shouldExecute ? 'run' : 'preview',
      connectionProvider: connection.provider_id
    }
    yield call(analysisCreatedEvent, metricEvent)
    yield put(setSecondSourceTable(null))
  } catch (e) {
    console.error(e)
  }
}

function* analysisCreatedEvent(payload) {
  try {
    const sourceDatasetId = yield select((state: RootState) => state.analysis.sourceDatasetId)
    const sourceDataset = yield select((state: RootState) => getEnrichedDatasetById(state, sourceDatasetId as string))
    yield put(analysisCreatedEventRequest({ ...payload, baseSourceType: sourceDataset.type }))
  } catch (e) {
    console.error(e)
  }
}

function* launchAnalysisJob() {
  try {
    const analysis = yield select((state: RootState) => state.analysis.selectedAnalysis)
    const connection = yield select((state: RootState) => state.analysis.connection)
    const { query, sqlParameters, geoColumn }: QuerySource = yield select(
      (state: RootState) => state.analysis.querySource
    )
    const mapId = yield select((state: RootState) => state.maps.currentMap?.id)
    let output = yield select((state: RootState) => state.analysis.destination)
    output = connection.provider_id === ProviderType.SNOWFLAKE ? output.toUpperCase() : output

    const { source, queryParameters } = parseQuery({
      query,
      sqlParameters,
      provider: connection.provider_id
    })
    const createTable = generateLaunchQuery(analysis, {
      provider: connection.provider_id,
      output,
      query: source,
      geoColumn
    })

    const job: ProcessingJob = yield call(createProcessingJob, {
      connection: connection.name,
      processingJobPayload: {
        query: createTable as string,
        queryParameters,
        metadata: {
          output,
          connection: connection.name,
          mapId,
          type: MapType.table,
          analysisType: analysis,
          provider: connection.provider_id
        }
      }
    })
    const metadata = job.response.metadata

    addAnalysisToLocalStorage(mapId, { jobId: job.jobId, connection: connection.name, metadata })
    yield pollingAnalysis(mapId, job.jobId, connection.name)
    const metricEvent = {
      mapId: mapId,
      analysisType: analysis,
      createdAs: 'save',
      timestamp: new Date().toString(),
      connectionProvider: connection.provider_id
    }
    yield call(analysisCreatedEvent, metricEvent)
    yield put(setSecondSourceTable(null))
  } catch (e) {
    console.error(e)
  }
}

function* checkAnalysisFromMap(action: PayloadAction<string>) {
  try {
    const mapId = action.payload
    const analyses = getArrayAnalysesFromMap(mapId)
    for (const analysis of analyses) {
      yield pollingAnalysis(mapId, analysis.jobId, analysis.connection)
    }
  } catch (e) {
    console.error(e)
  }
}

function* pollingAnalysis(mapId: string, jobId: string, connection: string) {
  try {
    const analysisStored = getAnalysis(mapId, jobId)
    const pollingPayload = { connection, jobId }

    const analysis: Analysis = {
      id: jobId,
      output: analysisStored.metadata.output,
      status: RequestStatus.Pending,
      type: MapType.table,
      analysisType: analysisStored.metadata.analysisType,
      provider: analysisStored.metadata.provider
    }

    yield put(addAnalysis(analysis))

    const jobStatus: ProcessingJobStatus = yield pollJobUntilFinished(() => checkProcessingJob(pollingPayload))

    if (jobStatus.status === JobStatusType.Success) {
      yield addLayer({
        mapId,
        connection,
        output: analysisStored.metadata.output,
        jobId,
        analysis
      })
      yield realoadDataTree({
        connection,
        output: analysisStored.metadata.output
      })
    } else if (jobStatus.status === JobStatusType.Failure) {
      yield put(setAnalysis({ ...analysis, status: RequestStatus.Rejected, error: jobStatus.error?.msg }))
      deleteAnalysisFromLocalStorage(mapId, analysis.id)
    }
  } catch (e) {
    console.error(e)
  }
}

function* deleteAnalysis({ mapId, jobId }) {
  try {
    deleteAnalysisFromLocalStorage(mapId, jobId)
    yield put(removeAnalysis(jobId))
  } catch (e) {
    console.error(e)
  }
}

function* realoadDataTree({ output, connection: connectionName }) {
  try {
    const connection: Connection = yield select((state: RootState) =>
      getConnectionByName(state, { connection: connectionName })
    )
    const { id, provider_id } = connection
    const path = output.split('.')
    path.pop()
    yield put(reloadDataTreeItemRequest({ connectionId: id, provider_id, items: path }))
  } catch (e) {
    console.error(e)
  }
}

function* addLayer({ mapId, output, connection: connectionName, jobId, analysis }) {
  try {
    const connection: Connection = yield select((state: RootState) =>
      getConnectionByName(state, { connection: connectionName })
    )
    const table: DataExplorerTable = yield call(getDataExplorerObject, {
      link: `/connections/${connection.id}/resources/${output}?type=${TreeObjectType.Table}`
    })

    const { type, format, source, geoColumn, nrows } = getDatasetSettingsForTable({ table })

    if (nrows === 0) {
      yield put(setAnalysis({ ...analysis, status: RequestStatus.Rejected, error: 'analysis.errorNoResult' }))
      deleteAnalysisFromLocalStorage(mapId, analysis.id)
    } else {
      const addLayerPayload = {
        mapId,
        type,
        source,
        format,
        geoColumn,
        connection: connectionName,
        provider: connection.provider_id
      }
      yield put(addLayerRequest(addLayerPayload))
      yield deleteAnalysis({ mapId, jobId })
    }
  } catch (e) {
    console.error(e)
  }
}

function* fetchColumnStats(
  action: PayloadAction<{
    connection: string
    source: string
    queryParameters?: QueryParameters | null
    column: string
    type: MapType
  }>
) {
  try {
    yield put(setColumnStatsStatus(RequestStatus.Pending))
    const { connection, source, queryParameters, column, type } = action.payload
    const stats = yield call(getAttributeStats, {
      connection,
      source,
      queryParameters,
      attribute: column,
      type,
      client: getClient()
    })
    yield put(setColumnStats({ attribute: column, max: stats.max, min: stats.min }))
    yield put(setColumnStatsStatus(RequestStatus.Fulfilled))
  } catch (e) {
    console.error(e)
    yield put(setColumnStatsStatus(RequestStatus.Rejected))
    yield put(setColumnStatsError(createApiError(e)))
  }
}

function* watchAnalysis() {
  yield takeLatest(launchAnalysisRequest.type, launchAnalysisJob)
  yield takeLatest(analysisToSQLPanelRequest.type, analysisToSQLPanel)
  yield takeLatest(checkAnalysesRequest.type, checkAnalysisFromMap)
  yield takeLatest(fetchColumnStatsRequest.type, fetchColumnStats)
}

export default function* analysisSagas() {
  try {
    yield watchAnalysis()
  } catch (e) {
    console.error('Error setting up analysisSagas', e)
  }
}
